/**
 * @module @fanini/shared-api/mutations/checkin
 * React-Query Mutation Hooks für Check-in-Operationen.
 *
 * @example
 * ```tsx
 * const createCheckin = useCreateCheckin();
 * // QR-Scan (barcode = pdfDriveId):
 * createCheckin.mutate({ eventId: 'event-1', barcode: 'BARCODE123', method: 'qr' });
 * // Manueller Check-in:
 * createCheckin.mutate({ eventId: 'event-1', userId: 'user-1', method: 'manual' });
 * ```
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CheckIn, CreateCheckIn } from '@fanini/shared-types';
import { useApiClient } from '../provider';
import { createCheckinQueries } from '../queries/checkin.queries';

/**
 * Mutation Hook für Check-in-Erstellung (QR oder manuell).
 * Invalidiert nach Erfolg die Check-in-Liste des Events.
 *
 * @returns TanStack Query Mutation
 */
export function useCreateCheckin() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const checkinQueries = createCheckinQueries(client);

  return useMutation({
    mutationFn: async (data: CreateCheckIn): Promise<CheckIn & { displayName?: string }> => {
      const response = await client.post<{
        success: boolean;
        checkIn: CheckIn;
        displayName?: string;
      }>('/api/checkins', data);
      return { ...response.checkIn, displayName: response.displayName };
    },
    onSuccess: (checkin) => {
      void queryClient.invalidateQueries({
        queryKey: checkinQueries.list(checkin.eventId).queryKey,
      });
    },
  });
}
